import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';

type CardElementV2 = HTMLElement;

type FilterV2 = {
    grade: string;
    subject: string;
};

// Константа для синхронизации скорости анимации
const ANIMATION_SPEED = 600; // ms - одинаковая скорость для Swiper и прокрутки кнопок

// Флаг для блокировки переключения во время анимации
let isAnimating = false;

function getCardsV2(): NodeListOf<CardElementV2> {
    return document.querySelectorAll('#teamV2 .teacher__card');
}

function getFilterButtonsV2(): NodeListOf<HTMLButtonElement> {
    return document.querySelectorAll('#teamV2 .filter-btn');
}

function getSubjectOptionsV2(): NodeListOf<HTMLLIElement> {
    return document.querySelectorAll('#teamV2 .filter-dropdown__option');
}

function getCardByFilterV2(filter: FilterV2): CardElementV2 | null {
    const cards = getCardsV2();
    for (const card of cards) {
        if (
            card.getAttribute('data-grades') === filter.grade &&
            card.getAttribute('data-subjects') === filter.subject
        ) {
            return card;
        }
    }
    return null;
}

function hasCardForFilterV2(filter: FilterV2): boolean {
    return !!getCardByFilterV2(filter);
}

function getFirstAvailableSubjectV2(grade: string): string | null {
    const cards = getCardsV2();
    for (const card of cards) {
        if (card.getAttribute('data-grades') === grade) {
            return card.getAttribute('data-subjects');
        }
    }
    return null;
}

function getFirstAvailableGradeV2(subject: string): string | null {
    const cards = getCardsV2();
    for (const card of cards) {
        if (card.getAttribute('data-subjects') === subject) {
            return card.getAttribute('data-grades');
        }
    }
    return null;
}

function setActiveCardV2(card: CardElementV2 | null) {
    // Блокируем переключение во время анимации
    if (isAnimating) {
        return;
    }

    const cards = getCardsV2();
    const activeCard = Array.from(cards).find(c => c.classList.contains('active'));

    if (activeCard && activeCard !== card) {
        isAnimating = true; // Блокируем дальнейшие переключения
        
        activeCard.classList.add('fading');
        activeCard.style.opacity = '0';
        activeCard.style.pointerEvents = 'none';
        setTimeout(() => {
            activeCard.classList.remove('active');
            activeCard.classList.remove('fading');
            activeCard.classList.add('disabled');
            activeCard.style.opacity = '';
            activeCard.style.pointerEvents = '';
            if (card) {
                card.classList.remove('disabled');
                card.classList.add('active');
                card.style.opacity = '0';
                setTimeout(() => {
                    card.style.opacity = '1';
                    // Инициализируем Swiper для карточек с множественными преподавателями
                    initSwiperForCardV2(card);
                    isAnimating = false; // Разблокируем переключения
                }, 10); // небольшой таймаут для плавности
            } else {
                isAnimating = false; // Разблокируем переключения
            }
        }, 400); // длительность анимации в ms
    } else {
        cards.forEach(c => {
            c.classList.remove('active');
            c.classList.add('disabled');
            c.style.opacity = '';
            c.style.pointerEvents = '';
        });
        if (card) {
            card.classList.remove('disabled');
            card.classList.add('active');
            card.style.opacity = '1';
            // Инициализируем Swiper для карточек с множественными преподавателями
            initSwiperForCardV2(card);
        }
    }
}

function initSwiperForCardV2(card: CardElementV2) {

    
    // Проверяем, есть ли в карточке Swiper
    const swiperContainer = card.querySelector('#team-swiper-v2') as HTMLElement;

    
    if (swiperContainer && !swiperContainer.classList.contains('swiper-initialized')) {

        
        try {
            const swiper = new Swiper(swiperContainer, {
                modules: [Navigation, Pagination],
                loop: false,
                slidesPerView: 1,
                spaceBetween: 20,
                speed: ANIMATION_SPEED,
                // Настройки для адаптивности
                breakpoints: {
                    320: {
                        slidesPerView: 1,
                        spaceBetween: 20,
                    },
                    768: {
                        slidesPerView: 1,
                        spaceBetween: 60,
                    },
                },
                // Обработчик смены слайда
                on: {
                    init(swiperInstance: any) {

                        updateCustomNavigationV2(swiperInstance, card);
                        // Прокручиваем к первой кнопке при инициализации
                        setTimeout(() => scrollToActiveNavItem(card, 0), 100);
                    },
                    slideChange(swiperInstance: any) {

                        updateCustomNavigationV2(swiperInstance, card);
                    }
                }
            });
            

            
            // Инициализируем кастомную навигацию
            initCustomNavigationV2(swiper, card);
            
            swiperContainer.classList.add('swiper-initialized');
            
        } catch (error) {
            console.error('Error initializing Swiper V2:', error);
        }
    } else {

    }
}

function initCustomNavigationV2(swiper: any, card: CardElementV2) {

    
    // Находим все элементы кастомной навигации
    const navItems = card.querySelectorAll('.nav-item');

    
    navItems.forEach((navItem, index) => {
        navItem.addEventListener('click', () => {

            // Переключаемся на соответствующий слайд
            swiper.slideTo(index);
        });
    });
}

function scrollToActiveNavItem(card: CardElementV2, activeIndex: number) {
    // Функция для прокрутки навигации к активной кнопке на мобильных
    const navigation = card.querySelector('.custom-navigation') as HTMLElement;
    const activeNavItem = card.querySelector(`.nav-item:nth-child(${activeIndex + 1})`) as HTMLElement;
    
    if (navigation && activeNavItem && window.innerWidth <= 767) {
        // Вычисляем позицию для прокрутки (центрируем активную кнопку)
        const targetScrollLeft = activeNavItem.offsetLeft - (navigation.offsetWidth / 2) + (activeNavItem.offsetWidth / 2);
        const currentScrollLeft = navigation.scrollLeft;
        const scrollDistance = targetScrollLeft - currentScrollLeft;
        
        // Синхронизируем с длительностью Swiper
        const duration = ANIMATION_SPEED;
        const startTime = performance.now();
        
        function animateScroll(currentTime: number) {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // Используем easing function для плавности (ease-out)
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            
            const newScrollLeft = currentScrollLeft + (scrollDistance * easeOutQuart);
            navigation.scrollLeft = newScrollLeft;
            
            if (progress < 1) {
                requestAnimationFrame(animateScroll);
            }
        }
        
        requestAnimationFrame(animateScroll);
    }
}

function updateCustomNavigationV2(swiper: any, card: CardElementV2) {

    
    const navItems = card.querySelectorAll('.nav-item');
    const activeIndex = swiper.activeIndex;
    
    navItems.forEach((navItem, index) => {
        const svg = navItem.querySelector('.nav-item-bg') as SVGElement;
        const path = svg?.querySelector('path') as SVGPathElement;
        const face = navItem.querySelector('.nav-item-face') as HTMLImageElement;
        
        if (index === activeIndex) {
            // Активное состояние
            navItem.classList.add('active');
            
            // Меняем цвет SVG на активный
            if (path && svg) {
                const activeColor = svg.getAttribute('data-active-color') || '#2271b1';
                path.setAttribute('fill', activeColor);
            }
            
            // Меняем картинку на активную, если есть
            if (face) {
                const activeSrc = face.getAttribute('data-active-src');
                if (activeSrc) {
                    face.src = activeSrc;
                }
            }
            
        } else {
            // Неактивное состояние
            navItem.classList.remove('active');
            
            // Возвращаем цвет SVG к стандартному
            if (path && svg) {
                const defaultColor = svg.getAttribute('data-default-color') || '#f0f0f1';
                path.setAttribute('fill', defaultColor);
            }
            
            // Возвращаем картинку к стандартной
            if (face) {
                const defaultSrc = face.getAttribute('data-default-src');
                if (defaultSrc) {
                    face.src = defaultSrc;
                }
            }
        }
    });
    
    // Прокручиваем навигацию к активной кнопке на мобильных
    scrollToActiveNavItem(card, activeIndex);
}

function setActiveButtonV2(grade: string) {
    getFilterButtonsV2().forEach(btn => {
        const btnGrade = btn.textContent?.trim() || '';
        if (btnGrade === grade) {
            btn.classList.add('active');
        } else {
            btn.classList.remove('active');
        }
    });
}

function setActiveSubjectOptionV2(subject: string) {
    getSubjectOptionsV2().forEach(option => {
        const optSubject = option.textContent?.trim() || '';
        if (optSubject === subject) {
            option.classList.add('active');
        } else {
            option.classList.remove('active');
        }
    });
    
    // Обновляем текст в выпадающем списке
    const selectedSpan = document.querySelector('#teamV2 .filter-dropdown__selected') as HTMLSpanElement;
    if (selectedSpan) {
        selectedSpan.textContent = subject;
    }
}

function updateFilterButtonsV2(subject: string) {
    // Кнопки всегда активны, ничего не делаем
}

function updateSubjectOptionsV2(grade: string) {
    getSubjectOptionsV2().forEach(option => {
        const subject = option.textContent?.trim() || '';
        const hasCard = hasCardForFilterV2({ grade, subject });
        if (hasCard) {
            option.classList.remove('disabled');
        } else {
            option.classList.add('disabled');
        }
    });
}

function handleFilterChangeV2(filter: FilterV2) {
    // Блокируем переключение во время анимации
    if (isAnimating) {
        return;
    }

    // Проверяем, есть ли карточка для выбранной пары
    let { grade, subject } = filter;
    if (!hasCardForFilterV2({ grade, subject })) {
        // Если нет карточки для пары, ищем первый доступный subject для grade
        const firstSubject = getFirstAvailableSubjectV2(grade);
        if (firstSubject) {
            subject = firstSubject;
        } else {
            // Если нет ни одного subject для grade, ищем первую доступную карточку вообще
            const firstCard = getCardsV2()[0];
            if (firstCard) {
                grade = firstCard.getAttribute('data-grades') || grade;
                subject = firstCard.getAttribute('data-subjects') || subject;
            }
        }
    }
    // Ставим active на карточку, кнопку и опцию
    const card = getCardByFilterV2({ grade, subject });
    setActiveCardV2(card);
    setActiveButtonV2(grade);
    setActiveSubjectOptionV2(subject);
    updateFilterButtonsV2(subject);
    updateSubjectOptionsV2(grade);
}

export function initTeacherFilterV2() {

    
    // Проверяем есть ли teachersV2 на странице
    const teachersV2Section = document.querySelector('#teamV2');
    if (!teachersV2Section) {

        return;
    }
    

    

    handleFilterChangeV2({ grade: 'ЕГЭ', subject: 'Математика' });

    // Кнопки фильтра по grade
    getFilterButtonsV2().forEach(btn => {
        btn.addEventListener('click', () => {
            const grade = btn.textContent?.trim() || '';
            const selectedSubject = document.querySelector('#teamV2 .filter-dropdown__option.active')?.textContent?.trim() || 'Биология';
            handleFilterChangeV2({ grade, subject: selectedSubject });
        });
    });

    // Клики по предметам
    getSubjectOptionsV2().forEach(option => {
        option.addEventListener('click', () => {
            const subject = option.textContent?.trim() || '';
            const activeBtn = document.querySelector('#teamV2 .filter-btn.active');
            const grade = activeBtn?.textContent?.trim() || 'ЕГЭ';
            // Если нет карточки для выбранной пары, handleFilterChangeV2 сам подберёт первую подходящую
            handleFilterChangeV2({ grade, subject });
        });
    });

    // --- Мобильный кастомный дропдаун ---
    const dropdown = document.querySelector('#teamV2 .filter-dropdown');
    const toggle = dropdown?.querySelector('.filter-dropdown__toggle') as HTMLButtonElement;
    const list = dropdown?.querySelector('.filter-dropdown__list') as HTMLUListElement;
    const selectedSpan = dropdown?.querySelector('.filter-dropdown__selected') as HTMLSpanElement;

    function closeDropdown() {
        dropdown?.classList.remove('open');
        toggle?.setAttribute('aria-expanded', 'false');
    }

    function openDropdown() {
        dropdown?.classList.add('open');
        toggle?.setAttribute('aria-expanded', 'true');
    }

    if (toggle && list && selectedSpan && dropdown) {
        // Инициализация выбранного предмета
        const activeOption = list.querySelector('.filter-dropdown__option.active');
        if (activeOption) {
            selectedSpan.textContent = activeOption.textContent;
        }

        // Открытие/закрытие по клику
        toggle.addEventListener('click', (e) => {
            e.stopPropagation();
            if (dropdown.classList.contains('open')) {
                closeDropdown();
            } else {
                openDropdown();
            }
        });

        // Клик вне дропдауна — закрыть
        document.addEventListener('click', (e) => {
            if (!dropdown.contains(e.target as Node)) {
                closeDropdown();
            }
        });

        // Выбор предмета
        list.querySelectorAll('.filter-dropdown__option').forEach(option => {
            option.addEventListener('click', () => {
                selectedSpan.textContent = option.textContent;
                closeDropdown();
            });
        });
    }
    
    // Обработчик изменения размера экрана для корректной работы навигации
    let resizeTimeout: number;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = window.setTimeout(() => {
            // Обновляем прокрутку навигации для всех активных карточек после resize
            const activeCards = document.querySelectorAll('#teamV2 .teacher__card.active');
            activeCards.forEach(card => {
                const swiper = (card.querySelector('.teachers-swiper') as any)?.swiper;
                if (swiper) {
                    scrollToActiveNavItem(card as CardElementV2, swiper.activeIndex);
                }
            });
        }, 250);
    });
}

document.addEventListener('DOMContentLoaded', () => {
    initTeacherFilterV2();
});
